import React, { memo, useReducer, useRef, useEffect } from 'react';
import { Space, Text, IconEdit, Divider } from '@redlink/components/atoms';
import { TextInput, Button } from '@redlink/components/molecules';
import { useLocale } from '@redlink/assets/locale';
import {
  useMutation,
  verifyWithPhoneNumberQuery,
  signUpWithPhoneNumberQuery,
} from '@redlink/services';
import { initialState, reducer, types } from './store';
import { AuthPageContainer } from '../registerWithPhone/authPageContainer';
import { createUseStyle } from '@redlink/assets/styles';
import { TouchableOpacity } from 'react-native';
import { useCounter } from '@redlink/core';
import { useNavigation, StackActions } from '@react-navigation/native';
import { useValues } from '@redlink/assets/values';

export const VerifyPhonePage = memo(({ navigation, route }) => {
  const { dictionary } = useLocale();
  const authContainerRef = useRef(null);
  const { styles, colors } = useStyle();
  const { routes } = useValues();
  const { dispatch: navigationDispatch } = useNavigation();
  const { phoneNumber } = route.params;

  const [{ code }, dispatch] = useReducer(reducer, initialState);

  const { counter, startCounter, counterStatus } = useCounter({
    start: 120,
    end: 0,
  });

  useEffect(() => startCounter(), []);

  const [verify, { loading: verifyIsBusy }] = useMutation(
    verifyWithPhoneNumberQuery,
    {
      done: async ({ VerifyWithPhoneNumber }) =>
        await authContainerRef.current.onAuthSuccess(VerifyWithPhoneNumber),
    }
  );

  const [signIn, { loading: signInIsBusy }] = useMutation(
    signUpWithPhoneNumberQuery,
    {
      done: () => startCounter(),
    }
  );

  return (
    <AuthPageContainer
      ref={authContainerRef}
      isBusy={verifyIsBusy || signInIsBusy}
      withGoogleAuth={false}
      title={dictionary.login}
    >
      <Text style={styles.hintText}>{dictionary.sentVerificationCode}</Text>
      <Space size={8} />
      <TouchableOpacity
        activeOpacity={0.7}
        style={styles.phoneEditButtonContainer}
        onPress={() =>
          navigationDispatch(StackActions.replace(routes.registerWithPhone.key))
        }
      >
        <Text style={styles.phoneEditButtonText}>{phoneNumber}</Text>
        <Space size={8} />
        <IconEdit color={colors.red1} size={18} />
      </TouchableOpacity>
      <Divider margin={30} />
      <TextInput
        require
        value={code}
        onChangeText={(value) =>
          dispatch({ type: types.SET_CODE, payload: value })
        }
        title={dictionary.enterPhoneVerificationCode}
        placeholder="******"
        keyboardType="numeric"
      />
      <Space />
      <Button
        style={styles.formActionContainer}
        title={dictionary.checkVerificationCode}
        variant="primary"
        onPress={() => {
          verify({
            phoneNumber,
            code,
          });
        }}
      />
      <Space size={40} />
      {counterStatus && <Text style={styles.timerText}>{counter}</Text>}
      {!counterStatus && (
        <Button
          onPress={() => {
            signIn({
              phoneNumber,
            });
          }}
          style={styles.actionButton}
          variant="link"
          title={dictionary.resendVerificationCode}
        />
      )}
    </AuthPageContainer>
  );
});

const useStyle = createUseStyle(({ colors, size }) => ({
  hintText: {
    color: colors.black,
  },
  formActionContainer: {
    flex: 1,
    minHeight: 50,
  },
  phoneEditButtonContainer: {
    flexDirection: 'row',
    backgroundColor: colors.gray7,
    borderRadius: 50,
    paddingHorizontal: 17,
    paddingVertical: 4,
    alignItems: 'center',
    alignSelf: 'flex-start',
  },
  phoneEditButtonText: {
    fontWeight: 'bold',
    marginTop: 4,
    color: colors.black,
  },
  timerText: {
    fontSize: 20,
    fontWeight: 'bold',
    textAlign: 'center',
    color: colors.black,
  },
}));
